// `MouseEvent` is aliased rather than imported under its own name: React's and
// the DOM's are different types, and importing React's unaliased shadows the
// global one that `document.addEventListener` below is typed against.
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn.js';
import { Tag } from '../primitives/Badge.js';
import { PulseDot, RouteProgress } from '../primitives/Feedback.js';
import { useTheme, type Density, type ThemeMode } from '../theme/ThemeProvider.js';
import { formatRate } from '../lib/format.js';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface NavItem {
  key: string;
  label: string;
  /** Monospace shorthand shown on the right of the rail: JV, TB, GRN … */
  tag?: string;
  badge?: number;
  /**
   * The screen's own address, which is what makes it a link rather than a
   * button.
   *
   * Supply it and the rail renders an `<a href>`: middle-click, ⌘/Ctrl-click and
   * "Open link in new tab" all work, the browser shows where a row goes on
   * hover, and the back button means what it says. A plain left click is still
   * handled in JavaScript — `onItemChange` fires and the anchor's default is
   * prevented — so nothing reloads and no state is lost.
   *
   * Omit it and the rail renders the button it always did. The shell has no
   * opinion about routing; it only offers the address the caller supplies.
   */
  href?: string;
  /**
   * The `permission_modules.code` this screen is, when it is one.
   *
   * The shell does no filtering with it — it is data the composing application
   * reads to decide what to pass in, because the rail must not be the place a
   * permission is decided. `App` filters `MODULES` on `can(item.permission)`
   * before handing them over, and the server refuses the same reads
   * independently, so hiding the row is a courtesy rather than the gate.
   *
   * Absent means "not a permission module": the Dashboard's own items are the
   * landing page and are shown to every signed-in user.
   */
  permission?: string;
}

export interface NavModule {
  key: string;
  label: string;
  hint?: string;
  items: NavItem[];
  /** The module's address — in practice its first item's. See {@link NavItem.href}. */
  href?: string;
}

export interface CompanyBrand {
  name: string;
  tagline?: string;
  mark?: string;
  logoUrl?: string;
  /**
   * The company's timezone. Set it and the bar carries a live clock under the
   * name — the date and time the application is working in, which is not
   * necessarily the one on the operator's laptop.
   */
  timezone?: string;
}

export interface CurrentUser {
  name: string;
  initials: string;
  jobTitle?: string;
}

/**
 * An ISO 4217 code, not a union of the two the book started with.
 *
 * It was `'PKR' | 'USD'`, which made the compiler enforce a rule the database
 * had already stopped holding: Setup → Currencies extends the `currency_code`
 * enum, so a book can name as many as somebody adds. The union meant a currency
 * that was genuinely postable was unselectable in the one control that chooses
 * one — and the type gave no hint that this was the reason.
 */
export type EntryCurrency = string;

/** One row of the currency register, as the top bar needs it. */
export interface CurrencyOption {
  code: string;
  name: string;
  symbol?: string | null;
  /** True for exactly one: the book's reporting currency, always rate 1. */
  isBase: boolean;
  /**
   * Latest SPOT rate into base. Null when the register has the currency but
   * `fx_rates` has never carried one for it — which is a real state on the day
   * somebody adds a currency, and shows as "no rate" rather than as 1.
   */
  rate: string | null;
}

/**
 * Currency and rate live in the chrome, not on the voucher.
 *
 * A book has one live rate per currency at a time, and asking for it on every
 * voucher header invited four people to type four different numbers for the same
 * afternoon. Choosing here means the rate is set once, by whoever is allowed to
 * set it, and every entry screen reads it.
 *
 * The list is the **register**, so a currency added under Setup → Currencies
 * appears here without a code change. `rate` is the rate for the selected
 * currency: editing it edits that currency's rate, not a permanently-USD one.
 */
export interface FxControl {
  /** The currency new vouchers are entered in. The base currency is rate 1. */
  currency: EntryCurrency;
  /** Every active currency in the book, base first. */
  currencies: CurrencyOption[];
  /** The selected currency's rate into base. '1' when base is selected. */
  rate: string;
  onCurrencyChange: (currency: EntryCurrency) => void;
  onRateChange: (rate: string) => void;
  /** The rate is a company-level number; only an admin edits it. */
  canEditRate?: boolean;
  /**
   * Make the typed rate the book's rate.
   *
   * Without this the field was a **session** setting: every screen in this tab
   * read it, no other tab did, and a reload lost it — so two people entering
   * vouchers on the same afternoon could be converting at two different
   * numbers, which is the thing having one rate in the chrome was meant to
   * prevent. Applying it writes a `SPOT` row into `fx_rates` for today, which
   * is what `GET /api/context` reads, what Setup → Tax & FX lists, and what
   * every rate field in the application opens on from then on.
   *
   * Rejects with an `Error` whose message is shown in the menu — most often the
   * server refusing the write for want of `finance.fx`, which is a real answer
   * and not one to swallow. Omit to leave the field session-only.
   */
  onApplyRate?: (currency: EntryCurrency, rate: string) => Promise<void>;
}

/**
 * Branch is tenancy, chosen once. An admin switches it; everyone else is told
 * which branch they are posting into and cannot move.
 */
export interface BranchControl {
  options: Array<{ code: string; name: string }>;
  active: string;
  onChange: (code: string) => void;
  canSwitch: boolean;
}

/**
 * Whether a click on a link is the browser's to handle or ours.
 *
 * The whole value of rendering nav as anchors is the modifiers: ⌘-click and
 * ctrl-click open a second tab, shift opens a window, alt downloads, and a
 * middle click opens a background tab. Every one of those is the *browser's*
 * gesture, and swallowing it with `preventDefault` is what makes a
 * single-page application feel like it has taken the web away from you.
 *
 * So: only a plain, unmodified primary click is handled in JavaScript.
 * Everything else falls through untouched, and the deep link the anchor points
 * at loads the same screen in the new tab.
 */
function isPlainClick(event: ReactMouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

// ─────────────────────────────────────────────────────────────────────────────
// Top bar
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One row in the search palette.
 *
 * `group` is a heading, not a filter — the palette shows every kind at once so
 * that `1131` finds the account and the vouchers that mention it in one pass,
 * which is the whole reason for having one box rather than four.
 */
export interface SearchHit {
  /** Unique within a result set; also the React key. */
  id: string;
  label: string;
  hint?: string;
  group: string;
}

export interface SearchControl {
  /**
   * Called on every keystroke, debounced by the palette.
   *
   * Async because most of what is worth finding lives in PostgreSQL. Returning
   * a plain array for the parts that do not — the nav itself — is fine.
   */
  find: (query: string) => Promise<SearchHit[]> | SearchHit[];
  onSelect: (hit: SearchHit) => void;
  placeholder?: string;
}

export interface TopBarProps {
  brand: CompanyBrand;
  modules: NavModule[];
  activeModule: string;
  onModuleChange: (key: string) => void;
  user: CurrentUser;
  /** The magnifier in the bar and the palette behind it. Omit to hide both. */
  search?: SearchControl;
  /** Entry currency and the live rate. The design puts it in the chrome because
   *  every screen in a dual-currency book needs it in view — and now because
   *  every entry screen reads its selection from here. */
  fx?: FxControl;
  branch?: BranchControl;
  roles?: Array<{ value: string; label: string; hint?: string }>;
  activeRole?: string;
  onRoleChange?: (role: string) => void;
  onSignOut?: () => void;
}

export function TopBar({
  brand,
  modules,
  activeModule,
  onModuleChange,
  user,
  search,
  fx,
  branch,
  roles,
  activeRole,
  onRoleChange,
  onSignOut,
}: TopBarProps) {
  const { resolvedTheme, setMode, density, setDensity } = useTheme();

  return (
    <header
      data-noprint="1"
      className="z-20 flex h-[var(--header-h)] shrink-0 items-center gap-3.5 border-b border-[var(--line)] bg-[var(--surface)] px-4"
    >
      {/* Brand */}
      <div className="flex shrink-0 items-center gap-2.5">
        {brand.logoUrl ? (
          <img
            src={brand.logoUrl}
            alt=""
            className="size-[26px] rounded-[7px] bg-[var(--surface-2)] object-contain"
          />
        ) : (
          <div className="grid size-[26px] place-items-center rounded-[7px] bg-[var(--accent-solid)] text-[13px] font-bold tracking-tight text-[var(--accent-ink)]">
            {brand.mark ?? brand.name.charAt(0)}
          </div>
        )}
        <div className="flex flex-col leading-[1.05]">
          <span className="text-[14px] font-semibold tracking-tight whitespace-nowrap">
            {brand.name}
          </span>
          {brand.timezone ? (
            <Clock timezone={brand.timezone} />
          ) : (
            brand.tagline && (
              <span className="bb-eyebrow text-[9.5px] tracking-[0.14em] whitespace-nowrap">
                {brand.tagline}
              </span>
            )
          )}
        </div>
      </div>

      {/*
        Module nav — desktop only.
        Below `lg` these move into the side rail, where there is vertical room
        for them: eight module names and a branch, FX and user control cannot
        share 1024px, and a horizontally scrolling nav is a nav people stop
        using. `SideRail` renders the same list under a `lg:hidden`.
      */}
      <nav
        aria-label="Modules"
        className="hidden h-full min-w-0 flex-[0_1_auto] items-stretch gap-0.5 overflow-x-auto lg:flex [scrollbar-width:none]"
      >
        {modules.map((m) => {
          const active = m.key === activeModule;
          const className = cn(
            'relative flex h-full shrink-0 cursor-pointer items-center gap-1.5 border-0 bg-transparent px-3',
            'text-[13px] font-medium whitespace-nowrap no-underline transition-colors',
            active
              ? 'text-[var(--ink)]'
              : 'text-[var(--ink-3)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]',
          );
          const inner = (
            <>
              {m.label}
              <span
                aria-hidden="true"
                className="absolute right-3 bottom-0 left-3 h-0.5 origin-left rounded-t-[2px] bg-[var(--accent)] transition-transform duration-[var(--dur)] ease-[var(--ease)]"
                style={{ transform: `scaleX(${active ? 1 : 0})` }}
              />
            </>
          );

          return m.href ? (
            <a
              key={m.key}
              href={m.href}
              title={m.hint}
              aria-current={active ? 'page' : undefined}
              onClick={(event) => {
                if (!isPlainClick(event)) return;
                event.preventDefault();
                onModuleChange(m.key);
              }}
              className={className}
            >
              {inner}
            </a>
          ) : (
            <button
              key={m.key}
              type="button"
              title={m.hint}
              aria-current={active ? 'page' : undefined}
              onClick={() => onModuleChange(m.key)}
              className={className}
            >
              {inner}
            </button>
          );
        })}
      </nav>

      <div className="flex-1" />

      {/* Search — the one box over accounts, vouchers, parties and the nav */}
      {search && <GlobalSearch {...search} modules={modules} />}

      {/*
        Branch — only where there is more than one.

        A book with a head office and nothing else was rendering a control whose
        entire content was the word `HO`: not a choice, not a warning, and not a
        fact anybody needed repeated on every screen, because with one branch
        every voucher is posted into it. The second branch brings it back, and
        the read-only form still renders for somebody who may not switch — that
        one *is* telling them something, namely which of several they are in.
      */}
      {branch && branch.options.length > 1 && <BranchMenu {...branch} />}

      {/* FX — the entry currency and the rate it converts at */}
      {fx && <FxMenu {...fx} />}

      {/*
        Everything about *you* — who you are signed in as, how the app looks,
        and the way out — behind one control.

        These were four: a five-option role switcher, a two-option density
        switcher, a theme button and a sign-out button, and between them they
        took more of the bar than the module nav did. None of them is touched
        more than once a session.
      */}
      <UserMenu
        user={user}
        roles={roles}
        activeRole={activeRole}
        onRoleChange={onRoleChange}
        onSignOut={onSignOut}
        theme={resolvedTheme}
        onThemeChange={setMode}
        density={density}
        onDensityChange={setDensity}
      />
    </header>
  );
}

/**
 * The date and time, under the company name.
 *
 * In the **company's** timezone, not the browser's. A book kept in Karachi and
 * opened from a laptop still set to London would otherwise show a clock that
 * disagrees with the date every new voucher is stamped with, and disagrees by a
 * whole day for five hours of every evening.
 *
 * It ticks once a minute rather than once a second: seconds are not information
 * anybody needs from a ledger, and a re-render a second is a re-render a second.
 * The first tick is aligned to the turn of the minute so the displayed time is
 * never up to 59 seconds stale.
 */
function Clock({ timezone }: { timezone: string }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    const align = setTimeout(
      () => {
        setNow(new Date());
        interval = setInterval(() => setNow(new Date()), 60_000);
      },
      (60 - new Date().getSeconds()) * 1000,
    );
    return () => {
      clearTimeout(align);
      if (interval) clearInterval(interval);
    };
  }, []);

  // Built per render rather than memoised: `Intl.DateTimeFormat` is cached by
  // the engine, and this runs once a minute.
  const text = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(now);

  return (
    <time
      dateTime={now.toISOString()}
      title={`Company time · ${timezone}`}
      className="bb-num text-[9.5px] whitespace-nowrap text-[var(--ink-3)]"
    >
      {text}
    </time>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Search
// ─────────────────────────────────────────────────────────────────────────────

/** How long to wait after the last keystroke before asking the API. */
const SEARCH_DEBOUNCE_MS = 160;

/**
 * The command palette behind the magnifier.
 *
 * A palette rather than an inline box, for one reason that is not aesthetic: the
 * results are the point, and there is no room for them in a bar that already
 * holds eight module names, a branch, an FX rate and a user menu. Opening over
 * the page gives the list the width to show `1131 · Accounts Receivable — level
 * 4` instead of an ellipsis, and it puts the caret where the eye already is.
 *
 * **Ctrl/⌘-K opens it from anywhere.** The listener is on the document and
 * checks the event target: typing `k` into a voucher's narration must insert a
 * `k`, so the shortcut is ignored while focus is in a field — with the
 * deliberate exception of ⌘K itself, which no text field claims.
 *
 * Requests are debounced and the last one wins. A palette that renders the
 * results of a query two keystrokes old is worse than a slow one, so each
 * response checks it is still the newest before it is allowed to paint.
 */
function GlobalSearch({
  find,
  onSelect,
  placeholder = 'Search accounts, vouchers, parties…',
  modules,
}: SearchControl & { modules: NavModule[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const latest = useRef(0);

  // ⌘K / Ctrl-K from anywhere, and `/` when not already typing.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        target?.isContentEditable === true;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
        return;
      }
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else {
      setQuery('');
      setHits([]);
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const term = query.trim();
    // Nav items are matched here, synchronously: they are a fixed list of a few
    // dozen and waiting on a network round trip to offer "Chart of Accounts"
    // would make the fastest result the slowest one.
    const navHits: SearchHit[] = term
      ? modules.flatMap((module) =>
          module.items
            .filter((item) =>
              `${module.label} ${item.label}`.toLowerCase().includes(term.toLowerCase()),
            )
            .map((item) => ({
              // `kind:qualifier:key`, with the qualifier empty — the same three
              // fields every hit uses, so the host can split once and route.
              id: `nav::${item.key}`,
              label: item.label,
              hint: module.label,
              group: 'Go to',
            })),
        )
      : [];

    if (!term) {
      setHits([]);
      return;
    }

    const attempt = ++latest.current;
    setHits(navHits);
    setBusy(true);

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const found = await find(term);
          if (attempt !== latest.current) return;
          setHits([...navHits, ...found]);
        } catch {
          // A failed lookup leaves the nav matches: the palette still navigates
          // when the API is down, which is when somebody most needs to get to a
          // different screen.
          if (attempt === latest.current) setHits(navHits);
        } finally {
          if (attempt === latest.current) setBusy(false);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, open, find, modules]);

  // A shorter result list must not leave the highlight past its end.
  useEffect(() => {
    setActive((index) => (index < hits.length ? index : 0));
  }, [hits.length]);

  const choose = (hit: SearchHit | undefined) => {
    if (!hit) return;
    onSelect(hit);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search"
        title="Search — Ctrl+K"
        className={cn(
          'flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] border px-2.5',
          'border-[var(--line)] bg-[var(--surface-2)] text-[var(--ink-3)]',
          'transition-colors hover:border-[var(--accent)] hover:text-[var(--ink)]',
        )}
      >
        <SearchIcon />
        <span className="hidden text-[12px] xl:inline">Search</span>
        <kbd
          className={cn(
            'hidden rounded-[4px] border border-[var(--line)] bg-[var(--surface)] px-1',
            'text-[10px] text-[var(--ink-3)] xl:inline',
          )}
        >
          Ctrl K
        </kbd>
      </button>

      {open &&
        createPortal(
          <div
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setOpen(false);
            }}
            className="fixed inset-0 z-[100] flex justify-center bg-black/40 px-4 pt-[12vh]"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Search"
              className={cn(
                'flex max-h-[70vh] w-full max-w-[600px] flex-col overflow-hidden',
                'rounded-[var(--radius)] border border-[var(--line)] bg-[var(--surface)]',
                'shadow-[0_24px_64px_rgba(0,0,0,0.32)]',
              )}
            >
              <div className="flex items-center gap-2.5 border-b border-[var(--line)] px-3.5">
                <SearchIcon />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setActive(0);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setOpen(false);
                    else if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      setActive((i) => (hits.length === 0 ? 0 : (i + 1) % hits.length));
                    } else if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      setActive((i) =>
                        hits.length === 0 ? 0 : (i - 1 + hits.length) % hits.length,
                      );
                    } else if (event.key === 'Enter') {
                      event.preventDefault();
                      choose(hits[active]);
                    }
                  }}
                  placeholder={placeholder}
                  aria-label="Search"
                  autoComplete="off"
                  spellCheck={false}
                  className={cn(
                    'h-12 w-full border-0 bg-transparent text-[14px] text-[var(--ink)] outline-none',
                    'placeholder:text-[var(--ink-3)]',
                  )}
                />
                {busy && <PulseDot />}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto py-1">
                {query.trim() === '' ? (
                  <p className="m-0 px-3.5 py-3 text-[12.5px] text-[var(--ink-3)]">
                    Type an account code or name, a voucher number, a party, or the name of a
                    screen. ↑↓ to move, Enter to open.
                  </p>
                ) : hits.length === 0 ? (
                  <p className="m-0 px-3.5 py-3 text-[12.5px] text-[var(--ink-3)]">
                    {busy ? 'Searching…' : `Nothing matches “${query.trim()}”.`}
                  </p>
                ) : (
                  hits.map((hit, index) => {
                    const heading = hit.group !== hits[index - 1]?.group;
                    return (
                      <div key={hit.id}>
                        {heading && (
                          <div className="px-3.5 pt-2.5 pb-1 text-[10px] font-medium tracking-[0.08em] text-[var(--ink-3)] uppercase">
                            {hit.group}
                          </div>
                        )}
                        <button
                          type="button"
                          onMouseEnter={() => setActive(index)}
                          onClick={() => choose(hit)}
                          className={cn(
                            'flex w-full cursor-pointer items-baseline gap-2.5 border-0 px-3.5 py-2 text-left',
                            index === active ? 'bg-[var(--accent-soft)]' : 'bg-transparent',
                          )}
                        >
                          <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--ink)]">
                            {hit.label}
                          </span>
                          {hit.hint && (
                            <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
                              {hit.hint}
                            </span>
                          )}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function SearchIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8.5 8.5L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Top-bar menus
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The one dropdown shape the chrome uses. Closes on outside click and on Escape,
 * because a panel that traps the user is worse than no panel.
 */
function Menu({
  trigger,
  title,
  children,
  width = 268,
}: {
  trigger: ReactNode;
  title: string;
  children: ReactNode;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        title={title}
        className={cn(
          'flex cursor-pointer items-center gap-1.5 rounded-[var(--radius-sm)] border px-2.5 py-[5px]',
          'transition-colors duration-[var(--dur-fast)]',
          open
            ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
            : 'border-[var(--line)] bg-[var(--surface-2)] hover:border-[var(--ink-3)]',
        )}
      >
        {trigger}
        <svg
          width="9"
          height="6"
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden="true"
          className="text-[var(--ink-3)]"
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={title}
          style={{ width }}
          className="bb-card absolute top-[calc(100%+7px)] right-0 z-30 flex flex-col overflow-hidden shadow-[var(--shadow-lg)]"
        >
          {children}
        </div>
      )}
    </div>
  );
}

function MenuOption({
  selected,
  onSelect,
  label,
  hint,
  trailing,
}: {
  selected: boolean;
  onSelect: () => void;
  label: ReactNode;
  hint?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2.5 border-0 px-3 py-2 text-left',
        selected ? 'bg-[var(--accent-soft)]' : 'bg-transparent hover:bg-[var(--surface-2)]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid size-[15px] shrink-0 place-items-center rounded-full border',
          selected
            ? 'border-[var(--accent-solid)] bg-[var(--accent-solid)]'
            : 'border-[var(--line)]',
        )}
      >
        {selected && <span className="size-[5px] rounded-full bg-[var(--accent-ink)]" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-medium text-[var(--ink)]">{label}</span>
        {hint && <span className="block text-[11px] text-[var(--ink-3)]">{hint}</span>}
      </span>
      {trailing}
    </button>
  );
}

function FxMenu({
  currency,
  currencies,
  rate,
  onCurrencyChange,
  onRateChange,
  canEditRate = false,
  onApplyRate,
}: FxControl) {
  const base = currencies.find((c) => c.isBase);
  const baseCode = base?.code ?? currency;
  const onBase = currency === baseCode;

  /**
   * What the register holds for the selected currency, against what is typed.
   *
   * The comparison is what makes Update mean something: a rate equal to the
   * stored one has nothing to apply, and a button that is always live invites
   * people to press it to find out whether it did anything.
   */
  const stored = currencies.find((c) => c.code === currency)?.rate ?? null;
  const dirty = rate.trim() !== '' && rate.trim() !== (stored ?? '').trim();

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);

  const applyRate = () => {
    if (!onApplyRate) return;
    setApplying(true);
    setApplyError(null);
    setApplied(null);
    onApplyRate(currency, rate.trim()).then(
      () => {
        setApplying(false);
        setApplied(rate.trim());
      },
      (cause: unknown) => {
        setApplying(false);
        setApplyError(cause instanceof Error ? cause.message : String(cause));
      },
    );
  };

  return (
    <Menu
      title="Entry currency and FX rate"
      trigger={
        <>
          {/* The rate says what it is on its own — "USD 1 = PKR 278.40" needs no
              label in front of it, and the two letters were taking the width a
              branch name wanted. */}
          <span className="bb-num text-[12px] font-medium whitespace-nowrap">
            {onBase ? `${baseCode} 1` : `${currency} 1 = ${baseCode} ${formatRate(rate)}`}
          </span>
          <PulseDot />
        </>
      }
    >
      <div className="bb-card-head">
        <span className="bb-eyebrow text-[10px]">Enter vouchers in</span>
      </div>

      {currencies.map((option) => (
        <MenuOption
          key={option.code}
          selected={currency === option.code}
          onSelect={() => onCurrencyChange(option.code)}
          label={
            option.isBase ? `${option.code} — base currency` : `${option.code} — ${option.name}`
          }
          hint={
            option.isBase
              ? 'Rate is always 1'
              : option.rate
                ? `1 ${option.code} = ${formatRate(option.rate)} ${baseCode}`
                : // A currency somebody added this morning has no rate yet. Saying
                  // so is the whole point: defaulting to 1 would post foreign
                  // amounts into the base ledger at par and look like it worked.
                  `No ${option.code}/${baseCode} rate yet — set one in Setup → Tax & FX`
          }
        />
      ))}

      {/* The rate field belongs to whichever currency is selected. On base there
          is nothing to edit: base = txn × 1 by definition. */}
      {!onBase && (
        <div className="flex items-center gap-2 border-t border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-2.5">
          <label htmlFor="fx-rate" className="bb-eyebrow shrink-0 text-[10px] whitespace-nowrap">
            {currency} rate
          </label>
          <input
            id="fx-rate"
            value={rate}
            readOnly={!canEditRate}
            inputMode="decimal"
            onChange={(event) => {
              // A fresh edit un-says the last result: leaving "Applied" beside a
              // number nobody has applied is the one wrong thing this panel
              // could say.
              setApplied(null);
              setApplyError(null);
              onRateChange(event.target.value);
            }}
            title={
              canEditRate
                ? `${currency} → ${baseCode}`
                : 'Only an administrator can change the rate'
            }
            className={cn(
              'bb-num h-7 w-full rounded-[var(--radius-sm)] border border-[var(--line)] px-2 text-right text-[12.5px]',
              'outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]',
              canEditRate
                ? 'bg-[var(--surface)] text-[var(--ink)]'
                : 'cursor-not-allowed bg-[var(--surface-2)] text-[var(--ink-3)]',
            )}
          />

          {/*
            Update — the difference between a number this tab is using and the
            number the book is kept at. Disabled where there is nothing to
            apply, so the enabled state is itself the statement that the field
            and the register disagree.
          */}
          {canEditRate && onApplyRate && (
            <button
              type="button"
              disabled={!dirty || applying}
              onClick={applyRate}
              title={
                dirty
                  ? `Save 1 ${currency} = ${rate} ${baseCode} as today's spot rate`
                  : `${currency} is already at this rate`
              }
              className={cn(
                'shrink-0 cursor-pointer rounded-[var(--radius-sm)] border px-2 py-[5px]',
                'text-[11.5px] font-medium whitespace-nowrap transition-colors',
                dirty && !applying
                  ? 'border-[var(--accent-solid)] bg-[var(--accent-solid)] text-[var(--accent-ink)]'
                  : 'cursor-not-allowed border-[var(--line)] bg-[var(--surface-2)] text-[var(--ink-3)]',
              )}
            >
              {applying ? 'Saving…' : 'Update'}
            </button>
          )}
        </div>
      )}

      {applyError && (
        <p className="m-0 border-t border-[var(--line-2)] px-3 py-2 text-[11px] text-[var(--debit)]">
          {applyError}
        </p>
      )}

      {applied && !applyError && (
        <p className="m-0 border-t border-[var(--line-2)] px-3 py-2 text-[11px] text-[var(--ink-2)]">
          1 {currency} = {applied} {baseCode} saved as today’s SPOT rate. Every rate field in the
          application opens on it, and Setup → Tax &amp; FX lists it by date. Vouchers already
          posted keep the rate they were posted at.
        </p>
      )}

      <p className="m-0 border-t border-[var(--line-2)] px-3 py-2 text-[11px] text-[var(--ink-3)]">
        {onBase
          ? `${baseCode} is the reporting currency. Every voucher is stored in it as well as in the currency it was entered in.`
          : canEditRate
            ? onApplyRate
              ? 'Typing changes this browser only. Update makes it the book’s rate — a SPOT row dated today, which every screen reads. base = txn × rate.'
              : 'Applies to every voucher entered from now on. base = txn × rate.'
            : 'Applies to every voucher you enter. Ask an administrator to change the rate.'}
      </p>
    </Menu>
  );
}

/**
 * `HO` + `Gujranwala` → `HO — Gujranwala`.
 *
 * The code and the place together, and nothing else: the word "Branch" in front
 * of it said what the control obviously is, and a name that repeats its own code
 * ("HO Head Office") is not doubled up here.
 */
function branchLabel(branch: { code: string; name: string } | undefined, fallback: string): string {
  if (!branch) return fallback;
  const name = branch.name.trim();
  if (!name) return branch.code;
  return name.toLowerCase().startsWith(branch.code.toLowerCase())
    ? name
    : `${branch.code} — ${name}`;
}

function BranchMenu({ options, active, onChange, canSwitch }: BranchControl) {
  const current = options.find((b) => b.code === active);
  const label = branchLabel(current, active);

  if (!canSwitch) {
    return (
      <div
        title="Your branch. An administrator can grant you access to others."
        className="flex shrink-0 items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface-2)] px-2.5 py-[5px]"
      >
        <span className="text-[12px] font-medium whitespace-nowrap text-[var(--ink-2)]">
          {label}
        </span>
      </div>
    );
  }

  return (
    <Menu
      title="Switch branch"
      trigger={<span className="text-[12px] font-medium whitespace-nowrap">{label}</span>}
    >
      <div className="bb-card-head">
        <span className="bb-eyebrow text-[10px]">Posting into</span>
      </div>
      {options.map((option) => (
        <MenuOption
          key={option.code}
          selected={option.code === active}
          onSelect={() => onChange(option.code)}
          label={option.name}
          trailing={<Tag>{option.code}</Tag>}
        />
      ))}
      <p className="m-0 border-t border-[var(--line-2)] px-3 py-2 text-[11px] text-[var(--ink-3)]">
        Switching branch changes which series a voucher number comes from.
      </p>
    </Menu>
  );
}

/**
 * A two-or-three way switch inside a menu.
 *
 * Theme and density are preferences with two values each; a radio list for each
 * would be six rows of chrome in a panel that also has to list five users. A
 * segmented row states the choice and the alternative on one line.
 */
function MenuSegment<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: T; label: string; hint?: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <span className="bb-eyebrow shrink-0 text-[10px]">{label}</span>
      <div className="ml-auto flex overflow-hidden rounded-[var(--radius-sm)] border border-[var(--line)]">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              title={option.hint}
              onClick={() => onChange(option.value)}
              className={cn(
                'cursor-pointer border-0 px-2.5 py-[5px] text-[11.5px] font-medium whitespace-nowrap',
                'transition-colors duration-[var(--dur-fast)]',
                selected
                  ? 'bg-[var(--accent-solid)] text-[var(--accent-ink)]'
                  : 'bg-[var(--surface-2)] text-[var(--ink-2)] hover:text-[var(--ink)]',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function UserMenu({
  user,
  roles,
  activeRole,
  onRoleChange,
  onSignOut,
  theme,
  onThemeChange,
  density,
  onDensityChange,
}: {
  user: CurrentUser;
  roles?: Array<{ value: string; label: string; hint?: string }>;
  activeRole?: string;
  onRoleChange?: (role: string) => void;
  onSignOut?: () => void;
  /** The theme in force — `system` already resolved to one of the two. */
  theme: 'light' | 'dark';
  onThemeChange: (mode: ThemeMode) => void;
  density: Density;
  onDensityChange: (density: Density) => void;
}) {
  return (
    <Menu
      title="Account, appearance and sign out"
      width={286}
      trigger={
        <>
          <span className="grid size-[22px] place-items-center rounded-full bg-[var(--accent-soft)] text-[10.5px] font-semibold text-[var(--accent)]">
            {user.initials}
          </span>
          <span className="hidden flex-col leading-[1.15] text-left sm:flex">
            <span className="text-[12.5px] font-medium whitespace-nowrap">{user.name}</span>
            {user.jobTitle && (
              <span className="text-[10.5px] whitespace-nowrap text-[var(--ink-3)]">
                {user.jobTitle}
              </span>
            )}
          </span>
        </>
      }
    >
      {/*
        The user switcher stands in for a login screen — these are real seeded
        accounts with the roles they actually hold, so the permission gates can
        be watched working rather than taken on trust.
      */}
      {roles && activeRole && onRoleChange && (
        <>
          <div className="bb-card-head">
            <span className="bb-eyebrow text-[10px]">Signed in as</span>
          </div>
          <div className="max-h-[210px] overflow-y-auto">
            {roles.map((role) => (
              <MenuOption
                key={role.value}
                selected={role.value === activeRole}
                onSelect={() => onRoleChange(role.value)}
                label={role.label}
                hint={role.hint}
              />
            ))}
          </div>
        </>
      )}

      <div className="border-t border-[var(--line-2)] bg-[var(--surface-2)]">
        {/* Picking a side also pins it: a book left on `system` flips to dark at
            sunset mid-entry, which is not what choosing Light meant. */}
        <MenuSegment
          label="Theme"
          value={theme}
          onChange={onThemeChange}
          options={[
            { value: 'light' as ThemeMode, label: 'Light' },
            { value: 'dark' as ThemeMode, label: 'Dark' },
          ]}
        />
        {/* Density is a real requirement, not decoration: it drives --pad, --row
            and --fs, so flipping it re-lays-out every row height. */}
        <MenuSegment
          label="Density"
          value={density}
          onChange={onDensityChange}
          options={[
            { value: 'comfortable' as Density, label: 'Comfortable', hint: '40px rows' },
            { value: 'compact' as Density, label: 'Compact', hint: '31px rows — more per screen' },
          ]}
        />
      </div>

      {onSignOut && (
        <button
          type="button"
          onClick={onSignOut}
          className="flex w-full cursor-pointer items-center gap-2.5 border-0 border-t border-[var(--line-2)] bg-transparent px-3 py-2.5 text-left text-[12.5px] font-medium text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M15 17l5-5-5-5" />
            <path d="M20 12H9" />
            <path d="M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" />
          </svg>
          Sign out
        </button>
      )}
    </Menu>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Side rail
// ─────────────────────────────────────────────────────────────────────────────

export interface SideRailProps {
  moduleLabel: string;
  items: NavItem[];
  activeItem: string;
  onItemChange: (key: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** Below `lg` the module nav lives here rather than in the top bar. */
  modules?: NavModule[];
  activeModule?: string;
  onModuleChange?: (key: string) => void;
  /** Below `lg`, float over the content instead of taking width from it. */
  overlay?: boolean;
}

/** `Journal Voucher` → `JV`. What a screen is called when there is no room to say it. */
function glyphFor(item: NavItem): string {
  if (item.tag) return item.tag;
  const initials = item.label
    .split(/\s+/)
    .map((word) => word.charAt(0))
    .join('');
  return initials.slice(0, 2).toUpperCase();
}

/**
 * Collapses to a tag column, not to nothing.
 *
 * It used to go to zero width and leave a 24px stub to bring it back, which
 * bought 244px of grid at the cost of not knowing where you were. The chart of
 * accounts already numbers every row and every screen in this module already has
 * a two-or-three letter shorthand in `tag` — JV, TB, COA — so the collapsed rail
 * shows those. They are the same abbreviations the vouchers themselves use, so
 * they are not glyphs anyone has to learn.
 */
export function SideRail({
  moduleLabel,
  items,
  activeItem,
  onItemChange,
  collapsed,
  onToggleCollapse,
  modules,
  activeModule,
  onModuleChange,
  overlay = false,
}: SideRailProps) {
  return (
    <aside
      data-noprint="1"
      className={cn(
        'shrink-0 overflow-hidden border-r border-[var(--line)] bg-[var(--surface)]',
        'transition-[width] duration-[var(--dur)] ease-[var(--ease)]',
        // Below `lg` an expanded rail floats over the ledger; from `lg` up it is
        // back in the flex row and pushes the content across as before.
        overlay &&
          'max-lg:absolute max-lg:inset-y-0 max-lg:left-0 max-lg:z-30 max-lg:shadow-[var(--shadow-lg)]',
      )}
      style={{ width: collapsed ? 'var(--rail-w-collapsed)' : 'var(--rail-w)' }}
    >
      <div
        className={cn(
          'flex h-full flex-col gap-0.5 overflow-x-hidden overflow-y-auto',
          collapsed ? 'w-[var(--rail-w-collapsed)] px-2 py-3' : 'w-[var(--rail-w)] p-3',
        )}
      >
        <div
          className={cn(
            'flex items-center pb-2.5',
            collapsed ? 'justify-center' : 'justify-between px-2',
          )}
        >
          {!collapsed && (
            <span className="bb-eyebrow overflow-hidden text-[10px] tracking-[0.14em] text-ellipsis whitespace-nowrap">
              {moduleLabel}
            </span>
          )}
          <button
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand navigation' : 'Collapse to tags'}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={!collapsed}
            className="cursor-pointer border-0 bg-transparent p-0.5 leading-none text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              {collapsed ? (
                <>
                  <path d="M11 6l6 6-6 6" />
                  <path d="M6 6v12" />
                </>
              ) : (
                <>
                  <path d="M13 6l-6 6 6 6" />
                  <path d="M18 6v12" />
                </>
              )}
            </svg>
          </button>
        </div>

        {/*
          The module nav, below `lg` only. The top bar hides it at that width and
          this takes over, so the two are never both on screen.
        */}
        {modules && activeModule && onModuleChange && !collapsed && (
          <nav aria-label="Modules" className="flex flex-col gap-0.5 lg:hidden">
            <span className="bb-eyebrow px-2.5 pb-1 text-[10px] tracking-[0.14em]">Modules</span>
            {modules.map((m) => {
              const active = m.key === activeModule;
              const className = cn(
                'flex h-[31px] w-full cursor-pointer items-center rounded-[var(--radius-sm)] border-0 px-2.5 text-left text-[12.5px] no-underline',
                active
                  ? 'bg-[var(--surface-2)] font-semibold text-[var(--ink)]'
                  : 'bg-transparent font-normal text-[var(--ink-3)] hover:bg-[var(--surface-2)] hover:text-[var(--ink-2)]',
              );

              return m.href ? (
                <a
                  key={m.key}
                  href={m.href}
                  title={m.hint}
                  aria-current={active ? 'page' : undefined}
                  onClick={(event) => {
                    if (!isPlainClick(event)) return;
                    event.preventDefault();
                    onModuleChange(m.key);
                  }}
                  className={className}
                >
                  {m.label}
                </a>
              ) : (
                <button
                  key={m.key}
                  type="button"
                  title={m.hint}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => onModuleChange(m.key)}
                  className={className}
                >
                  {m.label}
                </button>
              );
            })}
            <div className="my-2 h-px bg-[var(--line-2)]" />
            <span className="bb-eyebrow px-2.5 pb-1 text-[10px] tracking-[0.14em]">
              {moduleLabel}
            </span>
          </nav>
        )}

        {items.map((item, i) => {
          const active = item.key === activeItem;

          /**
           * One element, rendered as an anchor when the caller gave the screen
           * an address and as a button when it did not — so a rail item is a
           * real link wherever routing exists, without the two versions
           * drifting apart in appearance or behaviour.
           */
          const link = (className: string, children: ReactNode, label?: string) =>
            item.href ? (
              <a
                key={item.key}
                href={item.href}
                title={label}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                onClick={(event) => {
                  if (!isPlainClick(event)) return;
                  event.preventDefault();
                  onItemChange(item.key);
                }}
                className={cn(className, 'no-underline')}
                style={{ animationDelay: `${i * 0.018}s` }}
              >
                {children}
              </a>
            ) : (
              <button
                key={item.key}
                type="button"
                title={label}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                onClick={() => onItemChange(item.key)}
                className={className}
                style={{ animationDelay: `${i * 0.018}s` }}
              >
                {children}
              </button>
            );

          if (collapsed) {
            return link(
              cn(
                'bb-sweep-in relative grid h-[34px] w-full cursor-pointer place-items-center rounded-[var(--radius-sm)] border-0',
                active
                  ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                  : 'bg-transparent text-[var(--ink-3)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]',
              ),
              <>
                <span className="bb-mono text-[10.5px] font-semibold tracking-tight">
                  {glyphFor(item)}
                </span>
                {item.badge ? (
                  <span
                    aria-hidden="true"
                    className="absolute top-1 right-1 size-[6px] rounded-full bg-[var(--warn)]"
                  />
                ) : null}
              </>,
              item.label,
            );
          }

          return link(
            cn(
              'bb-sweep-in flex h-[33px] w-full cursor-pointer items-center gap-2.5 rounded-[var(--radius-sm)] border-0 px-2.5 text-left text-[13px]',
              active
                ? 'bg-[var(--accent-soft)] font-semibold text-[var(--accent)]'
                : 'bg-transparent font-normal text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]',
            ),
            <>
              <span
                aria-hidden="true"
                className="size-[5px] shrink-0 rounded-full bg-current opacity-50"
              />
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                {item.label}
              </span>
              {item.badge ? (
                <span className="bb-num rounded-full bg-[var(--warn-soft)] px-1.5 text-[10px] text-[var(--warn)]">
                  {item.badge}
                </span>
              ) : item.tag ? (
                <Tag>{item.tag}</Tag>
              ) : null}
            </>,
          );
        })}
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Letterhead
// ─────────────────────────────────────────────────────────────────────────────

export interface LetterheadProps {
  /** Trading name — the one in the top bar. */
  name: string;
  /** Registered name, printed under it when the two differ. */
  legalName?: string;
  logoUrl?: string;
  /** Address as one line. Built by the caller, which knows which parts are set. */
  location?: string;
  phone?: string;
  email?: string;
  ntn?: string;
  strn?: string;
  /** When the page was printed, in the company's timezone. */
  printedAt?: string;
}

/**
 * The company, at the top of every printed page and no screen.
 *
 * `hidden print:block` rather than a conditional render: the browser's print
 * dialog is not something React is told about, so the letterhead has to already
 * be in the document when the page is composed for paper.
 */
function Letterhead({
  name,
  legalName,
  logoUrl,
  location,
  phone,
  email,
  ntn,
  strn,
  printedAt,
}: LetterheadProps) {
  const contact = [phone, email].filter(Boolean).join(' · ');
  const statutory = [ntn && `NTN ${ntn}`, strn && `STRN ${strn}`].filter(Boolean).join(' · ');

  return (
    <div className="hidden pb-4 print:block">
      <div className="flex items-start justify-between gap-6 border-b border-black pb-2">
        <div className="flex items-start gap-3">
          {logoUrl && <img src={logoUrl} alt="" className="h-[38px] w-auto object-contain" />}
          <div>
            <div className="text-[15px] font-semibold">{name}</div>
            {legalName && legalName !== name && <div className="text-[11px]">{legalName}</div>}
            {location && <div className="text-[11px]">{location}</div>}
            {contact && <div className="text-[11px]">{contact}</div>}
          </div>
        </div>
        <div className="text-right text-[10.5px]">
          {statutory && <div>{statutory}</div>}
          {printedAt && <div>Printed {printedAt}</div>}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page header
// ─────────────────────────────────────────────────────────────────────────────

export function PageHeader({
  breadcrumb,
  title,
  description,
  actions,
  meta,
}: {
  breadcrumb?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <div data-report-head="1" className="flex items-end justify-between gap-6 pb-5">
      <div className="min-w-0">
        {breadcrumb && <div className="bb-eyebrow pb-2">{breadcrumb}</div>}
        <h1 className="m-0 text-[25px] leading-tight font-semibold tracking-tight text-[var(--ink)]">
          {title}
        </h1>
        {description && (
          <p className="mt-2 mb-0 max-w-[90ch] text-[13px] text-[var(--ink-2)] text-pretty">
            {description}
          </p>
        )}
        {meta && <div className="flex flex-wrap items-center gap-2 pt-3">{meta}</div>}
      </div>
      {actions && (
        <div data-noprint="1" className="flex shrink-0 items-center gap-2">
          {actions}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shell
// ─────────────────────────────────────────────────────────────────────────────

export interface AppShellProps extends Omit<TopBarProps, 'modules'> {
  modules: NavModule[];
  activeItem: string;
  onItemChange: (key: string) => void;
  /**
   * Something is in flight. Drives the bar across the top of the content, which
   * is the application's first answer to a nav click — see
   * {@link RouteProgress} for why it is that rather than a screenful of
   * placeholders.
   */
  busy?: boolean;
  /**
   * The company's letterhead, printed above every screen and shown on none.
   *
   * A statutory report leaves the building on paper, and a trial balance with
   * no company name on it is not a document — it is a screenshot. Rendering it
   * here rather than on each report means one place decides what a printed page
   * from this application says, and Setup → Company Profile is where the
   * contents are edited.
   */
  letterhead?: LetterheadProps;
  children: ReactNode;
}

export function AppShell({
  modules,
  activeModule,
  onModuleChange,
  activeItem,
  onItemChange,
  busy = false,
  letterhead,
  children,
  ...topBarProps
}: AppShellProps) {
  /**
   * Open on a desktop, shut on a phone.
   *
   * 244px of rail on a 375px screen leaves 131px of ledger, so below `lg` the
   * rail starts collapsed to its tag column and expands over the content rather
   * than beside it. Read once, at mount: this is a sensible starting position,
   * not a responsive binding, and re-deriving it on every resize would slam the
   * rail shut on somebody who had deliberately opened it.
   */
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1024,
  );
  const current = modules.find((m) => m.key === activeModule) ?? modules[0];

  /**
   * A module tab is a toggle when it is already the open one.
   *
   * Clicking Finance from Inventory switches module and opens the rail — that is
   * navigation. Clicking Finance *again* closes it, because the second click on
   * something you are already looking at can only mean "put it away". Without
   * this the rail could be opened from the top bar but only closed from a chevron
   * inside it, which is the asymmetry that made it feel stuck open.
   */
  const handleModuleChange = (key: string) => {
    if (key === activeModule) {
      setCollapsed((c) => !c);
      return;
    }
    setCollapsed(false);
    onModuleChange(key);
  };

  /** Narrow enough that the rail is covering the screen rather than sharing it. */
  const isOverlaid = () => typeof window !== 'undefined' && window.innerWidth < 1024;

  /**
   * Choosing a screen on a phone dismisses the rail it was chosen from.
   *
   * On a desktop it stays: the rail is beside the content, not on top of it, and
   * closing it after every click would make moving between two ledgers a chore.
   */
  const handleItemChange = (key: string) => {
    onItemChange(key);
    if (isOverlaid()) setCollapsed(true);
  };

  return (
    <div data-app="1" className="bb-fade-in flex h-screen flex-col overflow-hidden bg-[var(--bg)]">
      <TopBar
        modules={modules}
        activeModule={activeModule}
        onModuleChange={handleModuleChange}
        {...topBarProps}
      />

      <div className="relative flex min-h-0 flex-1">
        <SideRail
          moduleLabel={current?.label ?? ''}
          items={current?.items ?? []}
          activeItem={activeItem}
          onItemChange={handleItemChange}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
          modules={modules}
          activeModule={activeModule}
          // Inside the rail a module tap is pure navigation: the rail is already
          // open and closing it under the finger that just opened a module would
          // undo the tap.
          onModuleChange={onModuleChange}
          overlay={!collapsed}
        />

        {/* Tapping the page closes an overlaid rail, which is the gesture people
            already try. Desktop never sees it — there the rail is in flow. */}
        {!collapsed && (
          <button
            type="button"
            data-noprint="1"
            aria-label="Close navigation"
            onClick={() => setCollapsed(true)}
            className="absolute inset-0 z-20 cursor-default border-0 bg-[var(--bg)]/55 lg:hidden"
          />
        )}

        {/*
          The bar sits on this wrapper rather than inside the scroller: an
          absolutely positioned child of a scrolling element scrolls away with
          the content, and a progress indicator that can be scrolled off screen
          is not one.
        */}
        <div className="relative flex min-w-0 flex-1 flex-col">
          <RouteProgress active={busy} />

          <main className="min-w-0 flex-1 overflow-y-auto pb-14">
            {/*
            Keyed on the screen, so each one arrives rather than replacing the
            last mid-frame.

            The key is doing two jobs. It restarts the entry animation — without
            it React reuses the element and the new screen simply appears, which
            after a wait reads as a glitch — and it discards the outgoing
            screen's DOM, so a tall register cannot leave the scroll position of
            the short form that follows it halfway down the page.
          */}
            <div
              key={activeItem}
              className="bb-page-in mx-auto max-w-[var(--content-max)] px-4 pt-7 sm:px-[30px]"
            >
              {letterhead && <Letterhead {...letterhead} />}
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
